import { get, post, put, patch, del } from "../utils/api/apiCaller";
import apiClient from "../utils/api/apiClient";
import { ENDPOINTS } from "../utils/api/endpoints";

export interface GetProjectsParams {
  page?: number;
  limit?: number;
  q?: string;
  sortBy?: string;
  sortOrder?: string;
  status?: string;
  type?: string;
  lenderCompanyName?: string;
  isUserModule?: boolean;
}

export const getProjects = ({
  page,
  limit,
  q,
  sortBy,
  sortOrder,
  status,
  type,
  lenderCompanyName,
  isUserModule,
}: GetProjectsParams = {}) => {
  const query = new URLSearchParams();

  if (page != null) query.set("page", String(page));
  if (limit != null) query.set("limit", String(limit));
  if (q?.trim()) query.set("q", q.trim());
  if (sortBy) query.set("sortBy", sortBy);
  if (sortOrder) query.set("sortOrder", sortOrder);
  if (status) query.set("status", status);
  if (type) query.set("type", type);
  if (lenderCompanyName) query.set("lenderCompanyName", lenderCompanyName);
  if (isUserModule != null) query.set("isUserModule", String(isUserModule));

  const url = `${ENDPOINTS.PROJECTS.LIST}?${query.toString()}`;

  return get(url, null, undefined);
};

export const getProject = (id: string) => get(ENDPOINTS.PROJECTS.DETAIL(id));
export const markProjectComplete = (id: string) =>
  patch(ENDPOINTS.PROJECTS.MARK_COMPLETE(id), {}, { "x-role": "admin" });
export const getProjectOverview = (id: string) =>
  get(ENDPOINTS.PROJECTS.OVERVIEW(id));
export const getGcProjectOverview = (id: string) =>
  get(ENDPOINTS.PROJECTS.OVERVIEW(id), undefined, { "x-role": "gc" });
export const getLenderProjectOverview = (id: string) =>
  get(ENDPOINTS.PROJECTS.OVERVIEW(id), undefined, { "x-role": "lender" });
export const getOwnerProjectOverview = (id: string) =>
  get(ENDPOINTS.PROJECTS.OVERVIEW(id), undefined, { "x-role": "owner" });
export const getProjectAiaSummary = (
  id: string,
  role: "admin" | "lender" | "owner" | "gc" = "admin",
) => get(ENDPOINTS.PROJECTS.AIA_SUMMARY(id), undefined, { "x-role": role });
export const createShareLink = (
  id: string,
  data: {
    shareLinkType: string;
    role: string;
    resourceId?: string;
    emails?: string[];
  },
) => post(ENDPOINTS.PROJECTS.SHARE_LINKS(id), data, { "x-role": "admin" });
export const getProjectAiaBillingSummary = (
  id: string,
  role: "admin" | "lender" | "owner" | "gc" = "admin",
) =>
  get(ENDPOINTS.PROJECTS.AIA_BILLING_SUMMARY(id), undefined, {
    "x-role": role,
  });
export const exportProjectAiaBillingSummary = async (
  id: string,
  role: "admin" | "lender" | "owner" | "gc" = "admin",
): Promise<Blob> => {
  const { data, error } = await apiClient.call(
    "GET",
    ENDPOINTS.PROJECTS.AIA_BILLING_SUMMARY_EXPORT(id),
    { headers: { "x-role": role }, responseType: "blob" },
  );

  if (error || !data) throw new Error("Failed to export AIA billing summary");

  return data as Blob;
};
export const getPreconReport = (id: string) =>
  get(ENDPOINTS.PROJECTS.DETAIL(id), { preConstructionReport: true });
export const getGcPreconReview = (id: string) =>
  get(ENDPOINTS.PRECON.GC_REVIEW(id), undefined, { "x-role": "gc" });
export const getAdminPreconReview = (id: string) =>
  get(ENDPOINTS.PRECON.GC_REVIEW(id), undefined, { "x-role": "admin" });
export const getAdminPreconChecklist = (id: string) =>
  get(ENDPOINTS.ADMIN_CHECKLIST.GET(id), undefined, { "x-role": "admin" });
export const getLenderPreconChecklist = (id: string) =>
  get(ENDPOINTS.ADMIN_CHECKLIST.GET(id), undefined, { "x-role": "lender" });
export const getGcScheduleBudget = (id: string) =>
  get(ENDPOINTS.GC_BUDGET.SCHEDULE_BUDGET(id), undefined, { "x-role": "gc" });
export const createProject = (data: unknown) =>
  post(ENDPOINTS.PROJECTS.CREATE, data);
export const updateProject = (id: string, data: unknown) =>
  put(ENDPOINTS.PROJECTS.UPDATE(id), data);
export const deleteProject = (id: string) => del(ENDPOINTS.PROJECTS.DELETE(id));
export const getPreconDraft = (projectId: string) =>
  get(ENDPOINTS.PRECON.DRAFT(projectId));
export const savePreconDraft = (projectId: string, payload: unknown) =>
  post(ENDPOINTS.PRECON.SAVE_REPORT(projectId, false), payload);
export const publishPrecon = (projectId: string, payload: unknown) =>
  post(ENDPOINTS.PRECON.SAVE_REPORT(projectId, true), payload);

export const getGcRequirements = (projectId: string, params?: Record<string, unknown>) =>
  get(ENDPOINTS.GC_REQUIREMENTS.LIST(projectId), params);

export const getGcRequirementsView = (projectId: string) =>
  get(ENDPOINTS.GC_REQUIREMENTS.VIEW(projectId));
export const signGcRequirements = (
  projectId: string,
  data: unknown,
  publish = false,
) => post(ENDPOINTS.GC_REQUIREMENTS.SIGN(projectId, publish), data);
export const updateGcRequirement = (
  projectId: string,
  reqId: string,
  isAccepted: boolean,
) => patch(ENDPOINTS.GC_REQUIREMENTS.UPDATE(projectId, reqId), { isAccepted });

export const getGcProgress = (projectId: string) =>
  get(ENDPOINTS.GC_PROGRESS.GET(projectId));

export const updateGcProgress = (
  projectId: string,
  items: { id: string; status: string | null; note?: string }[],
  publish = false,
) => patch(ENDPOINTS.GC_PROGRESS.UPDATE(projectId, publish), { items });

export const getProjectDraws = (id: string) => get(ENDPOINTS.DRAWS.LIST(id));

export const uploadChecklistDocument = (
  projectId: string,
  itemId: string,
  value: { key: string; fileName: string },
) =>
  patch(ENDPOINTS.GC_CHECKLIST.UPLOAD_DOCUMENT(projectId, itemId), { value });

export const getProjectDocuments = (id: string) =>
  get(ENDPOINTS.DOCUMENTS.LIST(id));

export const downloadProjectDocument = (projectId: string, id: string) =>
  get(ENDPOINTS.DOCUMENTS.DOWNLOAD(projectId, id));

export const downloadDocumentBlob = async (
  projectId: string,
  id: string,
  useOwnerId = false,
): Promise<Blob | null> => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const base = process.env.REACT_APP_API_BASE_URL ?? "";
  const res = await fetch(
    `${base}${ENDPOINTS.DOCUMENTS.DOWNLOAD(projectId, id, useOwnerId)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-role": role ?? "",
      },
    },
  );
  if (!res.ok) return null;
  return res.blob();
};

export const uploadProjectDocuments = (projectId: string, items: unknown[]) =>
  post(ENDPOINTS.DOCUMENTS.UPLOAD(projectId), { items });

export const getAdminDrawComments = (projectId: string, drawId: string) =>
  get(ENDPOINTS.LENDER.COMMENTS(projectId, drawId));

export const postAdminDrawComment = (
  projectId: string,
  drawId: string,
  body: string,
) => post(ENDPOINTS.LENDER.COMMENTS(projectId, drawId), { body });
